import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Description as FileIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  EXPORT_FORMAT_CSV,
  EXPORT_FORMAT_DOCX,
  EXPORT_FORMAT_PDF,
} from "../../../constants";
import { ProjectFile } from "../../../types";
import { hostname } from "../../../utils";

interface FileViewerDialogProps {
  open: boolean;
  onClose: () => void;
  file: ProjectFile | null;
  projectId: string;
  companyId: string;
  getToken: () => Promise<string>;
  onSaved?: () => void;
}

export default function FileViewerDialog({
  open,
  onClose,
  file,
  projectId,
  companyId,
  getToken,
  onSaved,
}: FileViewerDialogProps) {
  const theme = useTheme();
  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (file) {
      setContent(file.content || "");
      setOriginalContent(file.content || "");
    }
  }, [file]);

  const hasChanges = content !== originalContent;
  const isCsvFile = file?.name?.toLowerCase().endsWith(".csv");

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExport = async (format: string) => {
    handleExportClose();
    if (!file?.id) return;

    setExporting(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/projects/${projectId}/files/${file.id}/export`,
        {
          params: { format, companyId },
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      // Get filename from content-disposition header or construct it
      const contentDisposition = response.headers["content-disposition"];
      let filename = `${file.name.split(".")[0]}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      setSnackbarMessage(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting file:", error);
      setSnackbarMessage(`Failed to export as ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const handleSave = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/projects/${projectId}/files/${file.id}`,
        { content, companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOriginalContent(content);
      onSaved?.();
    } catch (error) {
      console.error("Error saving file:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
  };

  const handleClose = () => {
    setContent("");
    setOriginalContent("");
    onClose();
  };

  const isImage = file?.mimeType?.startsWith("image/");
  const isCode =
    file?.mimeType?.includes("javascript") ||
    file?.mimeType?.includes("typescript") ||
    file?.mimeType?.includes("python") ||
    file?.mimeType?.includes("json") ||
    file?.mimeType?.includes("html") ||
    file?.mimeType?.includes("css") ||
    file?.name?.match(/\.(js|jsx|ts|tsx|py|json|html|css|md|yaml|yml|sh|sql)$/);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "80vh" },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileIcon color="primary" />
              <Typography variant="h6">{file?.name}</Typography>
              {hasChanges && (
                <Typography variant="caption" color="warning.main">
                  (unsaved)
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {!isImage && (
                <Tooltip title="Export">
                  <IconButton
                    size="small"
                    onClick={handleExportClick}
                    disabled={exporting}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Copy Content">
                <IconButton size="small" onClick={handleCopy}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {isImage ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <img
                src={`data:${file?.mimeType};base64,${content}`}
                alt={file?.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              value={content}
              onChange={(e) => setContent(e.target.value)}
              variant="outlined"
              sx={{
                height: "100%",
                "& .MuiOutlinedInput-root": {
                  height: "100%",
                  alignItems: "flex-start",
                  fontFamily: isCode ? "monospace" : "inherit",
                  fontSize: isCode ? "13px" : "14px",
                  "& fieldset": { border: "none" },
                },
                "& .MuiOutlinedInput-input": {
                  height: "100% !important",
                  overflow: "auto !important",
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {file?.path}
            {file?.size ? ` • ${(file.size / 1024).toFixed(1)} KB` : ""}
          </Typography>
          <Button onClick={handleClose}>Close</Button>
          {!isImage && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              startIcon={<SaveIcon />}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
      >
        <MenuItem
          onClick={() => handleExport(EXPORT_FORMAT_PDF)}
          disabled={exporting}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as PDF</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleExport(EXPORT_FORMAT_DOCX)}
          disabled={exporting}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as DOCX</ListItemText>
        </MenuItem>
        {isCsvFile && (
          <MenuItem
            onClick={() => handleExport(EXPORT_FORMAT_CSV)}
            disabled={exporting}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={copied || Boolean(snackbarMessage)}
        autoHideDuration={2000}
        onClose={() => {
          setCopied(false);
          setSnackbarMessage("");
        }}
        message={snackbarMessage || "Content copied to clipboard"}
      />
    </>
  );
}
